/*
 * Цветные темы оформления.
 *
 * Тема описывается двумя значениями: акцентным цветом и мотивом узора.
 * Всё остальное — оттенки наведения, цвет исходящих пузырей, пятна фона —
 * выводится из акцента по формуле.
 *
 * Почему так, а не таблицей готовых значений: восемь тем на две световые
 * схемы дали бы под сотню шестнадцатеричных литералов, которые невозможно
 * держать согласованными вручную. При выводе из одного цвета тема добавляется
 * одной строкой и гарантированно остаётся гармоничной.
 */

import { patternImage, type PatternId } from './patterns.js';

export interface ColorTheme {
	id: string;
	title: string;
	/** Основной цвет темы. Из него выводится всё остальное. */
	accent: string;
	pattern: PatternId;
}

export const COLOR_THEMES: ColorTheme[] = [
	{ id: 'crimson', title: 'Малиновая', accent: '#cd3782', pattern: 'classic' },
	{ id: 'cosmos', title: 'Космос', accent: '#5b6ee8', pattern: 'cosmos' },
	{ id: 'neon', title: 'Неон', accent: '#b14bf0', pattern: 'neon' },
	{ id: 'forest', title: 'Летний лес', accent: '#3fa05f', pattern: 'forest' },
	{ id: 'sunset', title: 'Закат', accent: '#e2683a', pattern: 'sunset' },
	{ id: 'sea', title: 'Море', accent: '#2b9fa8', pattern: 'sea' },
	{ id: 'graphite', title: 'Графит', accent: '#8a8a96', pattern: 'graphite' },
	{ id: 'plain', title: 'Простая', accent: '#3a7bd5', pattern: 'none' }
];

export const DEFAULT_THEME_ID = 'crimson';

export function themeById(id: string): ColorTheme {
	return COLOR_THEMES.find((t) => t.id === id) ?? COLOR_THEMES[0]!;
}

/* --- Работа с цветом ---------------------------------------------------- */

interface Hsl {
	h: number;
	s: number;
	l: number;
}

/** Разобрать `#rrggbb` в тон, насыщенность и светлоту. */
function hexToHsl(hex: string): Hsl {
	const value = hex.replace('#', '');
	const r = parseInt(value.slice(0, 2), 16) / 255;
	const g = parseInt(value.slice(2, 4), 16) / 255;
	const b = parseInt(value.slice(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const delta = max - min;
	const l = (max + min) / 2;

	if (delta === 0) return { h: 0, s: 0, l: l * 100 };

	const s = delta / (1 - Math.abs(2 * l - 1));

	let h: number;
	if (max === r) h = ((g - b) / delta) % 6;
	else if (max === g) h = (b - r) / delta + 2;
	else h = (r - g) / delta + 4;

	h *= 60;
	if (h < 0) h += 360;

	return { h, s: s * 100, l: l * 100 };
}

const hsl = (h: number, s: number, l: number) =>
	`hsl(${Math.round(((h % 360) + 360) % 360)} ${Math.round(Math.min(100, Math.max(0, s)))}% ${Math.round(Math.min(100, Math.max(0, l)))}%)`;

const hsla = (h: number, s: number, l: number, a: number) =>
	`hsl(${Math.round(((h % 360) + 360) % 360)} ${Math.round(s)}% ${Math.round(l)}% / ${a})`;

/* --- Переменные темы ---------------------------------------------------- */

/**
 * Набор переменных CSS для выбранной темы и световой схемы.
 *
 * Возвращает именно те токены, которые уже объявлены в `colors.css`, —
 * компоненты продолжают читать привычные имена и о темах не знают.
 */
export function themeVariables(
	theme: ColorTheme,
	scheme: 'dark' | 'light'
): Record<string, string> {
	const { h, s } = hexToHsl(theme.accent);
	const dark = scheme === 'dark';

	// Серая тема почти лишена насыщенности — пятна фона из неё выходят
	// грязными, поэтому им задаётся отдельный, чуть более живой предел.
	const spotSaturation = Math.max(s * 0.45, 12);

	const variables: Record<string, string> = {
		'--accent-primary': theme.accent,
		'--accent-hover': hsl(h, s, dark ? 62 : 44),
		'--accent-active': hsl(h, s, dark ? 44 : 36),
		'--accent-muted': hsla(h, s, 55, dark ? 0.16 : 0.12),
		'--accent-border': hsla(h, s, 55, 0.4),

		'--bubble-out-bg': dark ? hsl(h, s * 0.62, 30) : hsl(h, s * 0.75, 88),
		'--bubble-out-text': dark ? '#ffffff' : hsl(h, s * 0.7, 20),
		'--bubble-out-meta': dark ? 'rgba(255, 255, 255, 0.65)' : hsla(h, s * 0.6, 30, 0.6),

		'--chat-pattern-1': dark ? hsl(h, spotSaturation, 22) : hsl(h, spotSaturation, 94),
		'--chat-pattern-2': dark ? hsl(h + 28, spotSaturation, 19) : hsl(h + 28, spotSaturation, 95),
		'--chat-pattern-3': dark ? hsl(h - 22, spotSaturation, 17) : hsl(h - 22, spotSaturation, 93),

		'--chat-pattern-image': patternImage(
			theme.pattern,
			dark ? '#ffffff' : '#000000',
			dark ? 0.07 : 0.06
		)
	};

	return variables;
}
